import {
	AccountChecklistDetails,
	AccountChecklistItem,
	AccountChecklistItemEnum,
	ChecklistItemWithLabel
} from 'types/account';
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { getAccountChecklistData } from '@api/account';

export const useAccountSetupChecklistStore = defineStore('accountSetupChecklist', () => {
	const accountChecklistDetails = ref<AccountChecklistDetails | null>(null);
	const allTasks = ref<Array<ChecklistItemWithLabel>>([]);
	const showSuccessState = ref(false);

	const isLastChecklistItem = computed((): boolean => {
		return allTasks.value.length === 1;
	});

	/**
	 * Maps an AccountChecklistItem to a ChecklistItemWithLabel by adding a `label` string.
	 *
	 * The API response provides only structured enum values (e.g., `ACCT_SETUP_2FA`) without corresponding
	 * user-facing labels for display in the UI. This function maps each enum to a readable string that can be
	 * used as the key in key-value UI components (e.g., profile-account-setup-checklist.vue).
	 *
	 * If an enum is not explicitly mapped, an empty string is used as a fallback to avoid breaking the UI.
	 */
	function mapChecklistKeyValue(item: AccountChecklistItem): ChecklistItemWithLabel {
		const checklistMap: Record<AccountChecklistItemEnum, string> = {
			ACCT_SETUP_BANK_ACCOUNT: 'Setup bank account',
			ACCT_SETUP_VERIFY_MOBILE: 'Verify mobile number',
			ACCT_SETUP_2FA: 'Two-factor authentication',
			ACCT_SETUP_BENEFICIARY: 'Designated beneficiary'
		};
		return {
			...item,
			label: checklistMap[item.checklistItemEnum] ?? ''
		};
	}

	async function fetchAndStoreAccountSetupChecklistData(): Promise<void> {
		accountChecklistDetails.value = await getAccountChecklistData();
		allTasks.value = [
			...accountChecklistDetails.value.requiredTasks,
			...accountChecklistDetails.value.recommendedTasks
		].map(mapChecklistKeyValue);
	}

	function resetDisplaySuccessState(): void {
		showSuccessState.value = false;
	}

	function $reset(): void {
		accountChecklistDetails.value = null;
		allTasks.value = [];
		showSuccessState.value = false;
	}

	return {
		accountChecklistDetails,
		allTasks,
		isLastChecklistItem,
		fetchAndStoreAccountSetupChecklistData,
		resetDisplaySuccessState,
		showSuccessState,
		$reset
	};
});
